import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type } from "arktype";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { OverlayShell } from "@/components/ui/overlay";
import { format } from "@/lib/format";

import { FinanceiroCurrencyInput } from "../financeiro-currency-input";
import { BankMovementContext } from "./bank-movement-context";
import type { BillPrefill } from "./types";

const billForm = type({
	description: type("string.trim").to("string > 0"),
	amount_cents: "number.integer > 0",
	due_on: "string",
});

export function BillModal({ initial, onClose }: { initial?: BillPrefill; onClose: () => void }) {
	const queryClient = useQueryClient();
	const bank = initial?.bank;
	const create = useMutation(
		orpc.financeiro.bills.create.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: orpc.financeiro.key(),
				});
				toast.success(initial?.replaces_id === undefined ? "Conta registrada" : "Conta corrigida");
				onClose();
			},
			onError: () => toast.error("Não foi possível registrar a conta"),
		}),
	);
	const close = () => {
		if (!create.isPending) {
			onClose();
		}
	};
	const {
		control,
		register,
		handleSubmit,
		trigger,
		formState: { isValid },
	} = useForm<typeof billForm.infer>({
		resolver: arktypeResolver(billForm),
		mode: "onChange",
		defaultValues: {
			description: initial?.description ?? "",
			amount_cents: bank?.amount_cents ?? initial?.amount_cents ?? 0,
			due_on: initial?.due_on ?? format.today(),
		},
	});
	useEffect(() => {
		void trigger();
	}, [trigger]);
	const correcting = initial?.replaces_id !== undefined;
	let title = correcting ? "Corrigir conta" : "Nova conta";
	let guidance = "Registre o que a clínica precisa pagar e até quando.";

	if (bank !== undefined) {
		title = "Registrar conta paga";
		guidance = "A saída bancária será vinculada automaticamente à conta.";
	}

	return (
		<OverlayShell label={title} onClose={close} className="items-center justify-center p-4">
			<form
				data-component="financeiro-bill-modal"
				aria-busy={create.isPending}
				onSubmit={handleSubmit((value) =>
					create.mutate({
						...value,
						amount_cents: bank?.amount_cents ?? value.amount_cents,
						replaces_id: initial?.replaces_id,
						paid_on: bank?.effective_on,
						bank_transaction_id: bank?.id,
					}),
				)}
				className="overlay-panel max-w-md gap-4 p-5"
			>
				<div>
					<h2 className="type-section-title text-foreground">{title}</h2>
					<p className="mt-1 text-xs text-muted-foreground">{guidance}</p>
				</div>

				{bank !== undefined && <BankMovementContext movement={bank} />}

				<label className="flex flex-col gap-1.5 text-sm font-medium">
					Fornecedor ou descrição
					<Input {...register("description")} disabled={create.isPending} autoFocus />
				</label>

				<div className="grid grid-cols-2 gap-3">
					<label className="flex flex-col gap-1.5 text-sm font-medium">
						Valor
						<Controller
							control={control}
							name="amount_cents"
							render={({ field }) => (
								<FinanceiroCurrencyInput
									valueCents={field.value}
									onValueCentsChange={field.onChange}
									disabled={create.isPending || bank !== undefined}
									slot="amount"
								/>
							)}
						/>
					</label>
					<label className="flex flex-col gap-1.5 text-sm font-medium">
						Vencimento
						<Controller
							control={control}
							name="due_on"
							render={({ field }) => (
								<DatePicker
									value={field.value}
									onChange={(value) => value !== null && field.onChange(value)}
									disabled={create.isPending}
									containerClassName="w-full"
									className="w-full justify-between"
								/>
							)}
						/>
					</label>
				</div>

				<div className="flex justify-end gap-2">
					<Button type="button" variant="ghost" disabled={create.isPending} onClick={close}>
						Cancelar
					</Button>
					<Button type="submit" disabled={!isValid || create.isPending}>
						{create.isPending && <Loader2 className="animate-spin" />}
						{create.isPending && "Salvando"}
						{!create.isPending && correcting && "Salvar correção"}
						{!create.isPending && !correcting && "Registrar"}
					</Button>
				</div>
			</form>
		</OverlayShell>
	);
}
